import React, { useState, useEffect } from 'react';
import './DashboardPage.css';
import { supabase } from '../supabaseClient';
import ReportPreview from './ReportPreview';

const MODULE_META = [
  { key: 'module1', label: 'Слух и фонематика', fullLabel: 'Слуховое и фонематическое восприятие', topics: ['Дифференциация звуков', 'Дифференциация музыки', 'Восприятие ритма', 'Фонематическое восприятие'] },
  { key: 'module2', label: 'Зрение', fullLabel: 'Зрительное восприятие', topics: ['Различение цвета', 'Восприятие формы', 'Восприятие величины', 'Восприятие в наложении'] },
  { key: 'module3', label: 'Пространство', fullLabel: 'Пространственные представления', topics: ['Ориентировка в пространстве', 'Пазлы', 'Мысленное вращение'] },
  { key: 'module4', label: 'Понимание речи', fullLabel: 'Импрессивная речь', topics: ['Пассивный словарь', 'Словоизменение', 'Отрицания и исключения'] },
  { key: 'module5', label: 'Активная речь', fullLabel: 'Активная речь', topics: ['Лексика', 'Грамматика', 'Словообразование', 'Слоговая структура'] },
  { key: 'module6', label: 'Осведомлённость', fullLabel: 'Общая осведомлённость', topics: ['Части суток', 'Времена года', 'Дни недели'] },
  { key: 'module7', label: 'Мышление', fullLabel: 'Мышление', topics: ['4 лишний', 'Чего не хватает', 'Группы', 'Этапы'] },
];

const TEST_TO_MODULE_MAP = {
  module1: ['SoundTest', 'MusicPerception', 'RhythmTest', 'PhonemicPerception', 'SoundInWord', 'SoundPosition', 'sound', 'musicperception', 'rhythm', 'phonemicperception', 'soundinword', 'soundposition'],
  module2: ['VisionTest', 'SizeTest', 'VisionPerception', 'vision', 'size', 'visionperception'],
  module3: ['SpatialDirections', 'Puzzle', 'SeguinBoard', 'Mentalrotation', 'space', 'puzzle', 'seguinboard', 'mentalrotation'],
  module4: ['VocabularyTest', 'WordFormation', 'WordFormationDiminutive', 'AnimalBabies', 'VerbsFormation', 'NegationsTest', 'MultiStepInstr', 'SentenceComprehension', 'StorySequence', 'vocabulary', 'wordformation', 'wordformationdiminutive', 'animalbabies', 'verbsformation', 'negationstest', 'multistepinstr', 'sentencecomprehension', 'storysequence'],
  module5: ['Generalization', 'Antonyms', 'Verbs', 'SyllableStructure', 'generalization', 'antonyms', 'verbs', 'syllablestructure'],
  module6: ['DailyRoutine', 'Seasons', 'DaysOfWeek', 'Clocks', 'ClockBuilder', 'dailyroutine', 'seasons', 'daysofweek', 'clocks', 'clockbuilder'],
  module7: ['FindOddOne', 'MissingItem', 'GroupPictures', 'LifeStages', 'AnimalSorting', 'findoddone', 'missingitem', 'grouppictures', 'lifestages', 'animalsorting'],
};

// НОВОЕ: человекочитаемые названия по каждому реальному тесту (не по модулю в целом) —
// раньше их не было, и попап показывал сырые ключи (SoundTest, phonemicperception и т.д.).
// Ключи в нижнем регистре — оба варианта написания (PascalCase/lowercase) из TEST_TO_MODULE_MAP
// ведут на одно и то же название через приведение к нижнему регистру при поиске.
const TEST_LABELS = {
  soundtest: 'Различение звуков',
  musicperception: 'Восприятие музыки',
  rhythmtest: 'Восприятие ритма',
  phonemicperception: 'Фонематическое восприятие',
  soundinword: 'Звук в слове',
  soundposition: 'Позиция звука',
  visiontest: 'Различение цвета и формы',
  sizetest: 'Восприятие величины',
  visionperception: 'Восприятие в наложении',
  spatialdirections: 'Ориентировка в пространстве',
  puzzle: 'Пазлы',
  seguinboard: 'Доска Сегена',
  mentalrotation: 'Мысленное вращение',
  vocabularytest: 'Пассивный словарь',
  wordformation: 'Словообразование',
  wordformationdiminutive: 'Уменьшительные формы',
  animalbabies: 'Детёныши животных',
  verbsformation: 'Образование глаголов',
  negationstest: 'Отрицания',
  multistepinstr: 'Многоступенчатые инструкции',
  sentencecomprehension: 'Понимание предложений',
  storysequence: 'Последовательность рассказа',
  generalization: 'Обобщение',
  antonyms: 'Антонимы',
  verbs: 'Глаголы',
  syllablestructure: 'Слоговая структура',
  dailyroutine: 'Части суток',
  seasons: 'Времена года',
  daysofweek: 'Дни недели',
  clocks: 'Часы',
  clockbuilder: 'Собери часы',
  findoddone: '4 лишний',
  missingitem: 'Чего не хватает',
  grouppictures: 'Группировка картинок',
  lifestages: 'Этапы жизни',
  animalsorting: 'Сортировка животных', // раньше отсутствовало — именно поэтому "Мышление" показывало не все пункты
};

const calculateModuleScores = (rawScores) => {
  const moduleScores = {};
  const safeScores = rawScores || {};
  Object.keys(TEST_TO_MODULE_MAP).forEach(moduleKey => {
    const testKeys = TEST_TO_MODULE_MAP[moduleKey];
    const validScores = testKeys.map(tKey => safeScores[tKey]).filter(score => score !== undefined && score !== null);
    if (validScores.length > 0) {
      const avg = validScores.reduce((a, b) => a + b, 0) / validScores.length;
      moduleScores[moduleKey] = Math.round(avg);
    } else {
      // ИСПРАВЛЕНО: было 0 — неотличимо от честного нулевого результата. null = не пройдено.
      moduleScores[moduleKey] = null;
    }
  });
  return moduleScores;
};

const aggregateAllReports = (allReports) => {
  const result = {};
  MODULE_META.forEach(m => {
    // ИСПРАВЛЕНО: было `> 0`, что также отфильтровывало честные низкие/нулевые баллы
    const passedReports = allReports.filter(r => r.scores[m.key] !== null && r.scores[m.key] !== undefined);
    if (passedReports.length > 0) {
      passedReports.sort((a, b) => new Date(b.date) - new Date(a.date));
      const latest = passedReports[0];
      result[m.key] = { score: latest.scores[m.key], date: new Date(latest.date).toLocaleDateString('ru-RU') };
    } else {
      result[m.key] = { score: null, date: null };
    }
  });
  return result;
};

const TRAINER_URL = 'https://ezspeech.ai/';
const PRACTICE_THRESHOLD = 70;

const SIZE = 400;
const CENTER = SIZE / 2;
const RADIUS = 145;
const RINGS = 4;
const COUNT = MODULE_META.length;

function pointAt(index, radius) {
  const angle = (Math.PI * 2 * index) / COUNT - Math.PI / 2;
  return { x: CENTER + radius * Math.cos(angle), y: CENTER + radius * Math.sin(angle) };
}

const RadarSvg = ({ scores, id, muted, onModuleHover, onModuleLeave, hoveredModule }) => {
  const rings = Array.from({ length: RINGS }, (_, r) => {
    const ringRadius = (RADIUS * (r + 1)) / RINGS;
    return MODULE_META.map((_, i) => pointAt(i, ringRadius)).map((p) => `${p.x},${p.y}`).join(' ');
  });

  const dataPoints = MODULE_META.map((m, i) => pointAt(i, RADIUS * ((scores[m.key] ?? 0) / 100)));
  const dataPolygon = dataPoints.map((p) => `${p.x},${p.y}`).join(' ');

  return (
    <svg viewBox={`-40 -20 ${SIZE + 80} ${SIZE + 40}`} className="dash-radar-svg" role="img" aria-label="Диаграмма результатов">
      <defs>
        <linearGradient id={`grad-${id}`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={muted ? '#9a9a92' : '#312e81'} stopOpacity={muted ? 0.25 : 0.5} />
          <stop offset="100%" stopColor={muted ? '#c7c5bb' : '#7c9885'} stopOpacity={muted ? 0.2 : 0.4} />
        </linearGradient>
        {!muted && (
          <filter id={`glow-${id}`} x="-40%" y="-40%" width="180%" height="180%">
            <feGaussianBlur stdDeviation="5" result="blur" />
            <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        )}
      </defs>

      {rings.map((pts, i) => (<polygon key={i} points={pts} fill="none" stroke="#e6e4dc" strokeWidth={1} />))}
      {MODULE_META.map((_, i) => {
        const p = pointAt(i, RADIUS);
        return <line key={i} x1={CENTER} y1={CENTER} x2={p.x} y2={p.y} stroke="#e6e4dc" strokeWidth={1} />;
      })}

      <polygon points={dataPolygon} fill={`url(#grad-${id})`} stroke={muted ? '#9a9a92' : '#312e81'} strokeWidth={2} strokeLinejoin="round" filter={!muted ? `url(#glow-${id})` : undefined} />
      {dataPoints.map((p, i) => (<circle key={i} cx={p.x} cy={p.y} r={8} fill="#ffffff" stroke={muted ? '#9a9a92' : '#312e81'} strokeWidth={4} />))}

      {MODULE_META.map((m, i) => {
        const labelPos = pointAt(i, RADIUS + 34);
        const score = scores[m.key];
        const isHovered = hoveredModule === m.key;
        return (
          <g key={m.key} style={{ cursor: 'pointer' }}>
            <circle
              cx={labelPos.x}
              cy={labelPos.y}
              r={40}
              fill="transparent"
              style={{ pointerEvents: 'all' }}
              onMouseEnter={() => onModuleHover(m.key)}
              onMouseLeave={onModuleLeave}
            />
            <text x={labelPos.x} y={labelPos.y - 4} textAnchor="middle" className={`dash-axis-label ${isHovered ? 'active' : ''}`} style={{ pointerEvents: 'none' }}>{m.label}</text>
            {/* ИСПРАВЛЕНО: было `!== undefined`, теперь не-пройдено это null, а не undefined */}
            <text x={labelPos.x} y={labelPos.y + 16} textAnchor="middle" className={`dash-axis-value ${isHovered ? 'active' : ''}`} style={{ pointerEvents: 'none' }}>{score !== null && score !== undefined ? `${score}%` : '—'}</text>
          </g>
        );
      })}
    </svg>
  );
};

const ReportRadarCard = ({ scores, rawScores, taskScores, muted, onOpen, dateOptions, selectedDate, onDateChange, currentLabel, emptyHint, id }) => {
  const [hoveredModule, setHoveredModule] = useState(null);
  const hoveredMeta = MODULE_META.find((m) => m.key === hoveredModule);
  const hoveredScore = hoveredModule ? scores?.[hoveredModule] : null;
  const needsPractice = hoveredScore !== null && hoveredScore !== undefined && hoveredScore < PRACTICE_THRESHOLD;

  const getTaskBreakdown = () => {
    if (!hoveredModule || !rawScores) return [];
    const testKeys = TEST_TO_MODULE_MAP[hoveredModule] || [];
    const breakdown = [];
    const seen = new Set();

    testKeys.forEach(tKey => {
      if (rawScores[tKey] !== undefined && rawScores[tKey] !== null) {
        // ИСПРАВЛЕНО: раньше показывали tKey напрямую (сырой ключ вроде "SoundTest").
        // Теперь ищем человекочитаемое название по словарю, приводя ключ к нижнему регистру
        // (PascalCase и lowercase варианты одного и того же теста ведут на одно название).
        const label = TEST_LABELS[tKey.toLowerCase()] || tKey;
        if (!seen.has(label)) {
          seen.add(label);
          breakdown.push({ name: label, score: rawScores[tKey] });
        }
      }
    });

    return breakdown;
  };

  const taskBreakdown = getTaskBreakdown();

  return (
    <div className="dash-card">
      <div className="dash-card-top">
        {dateOptions ? (
          <select className="dash-date-select" value={selectedDate} onChange={(e) => onDateChange(e.target.value)}>
            <option value="none">Выбрать дату</option>
            {dateOptions.map((d) => (<option key={d.id} value={d.id}>{d.label}</option>))}
          </select>
        ) : (
          <span className="dash-card-date">{currentLabel}</span>
        )}

        {scores && (
          <div style={{ display: 'flex', gap: '14px' }}>
            <button type="button" onClick={onOpen} style={{ background: 'transparent', border: 'none', fontSize: '13px', fontWeight: '600', color: '#312e81', cursor: 'pointer' }}>Открыть</button>
          </div>
        )}
      </div>

      {!scores ? (
        <div className="dash-card-empty">{emptyHint}</div>
      ) : (
        <div className="dash-radar-zone" style={{ position: 'relative' }}>
          <RadarSvg scores={scores} id={id} muted={muted} hoveredModule={hoveredModule} onModuleHover={setHoveredModule} onModuleLeave={() => setHoveredModule(null)} />

          {hoveredMeta && (
            <div style={{ position: 'absolute', top: '20px', right: '20px', width: '240px', background: 'white', border: '1px solid #e5e1d8', borderRadius: '16px', boxShadow: '0 10px 30px -12px rgba(30, 41, 59, 0.2)', padding: '16px', zIndex: 10 }}>
              <p style={{ fontWeight: 700, fontSize: '14px', color: '#312e81', margin: '0 0 8px' }}>{hoveredMeta.fullLabel}</p>
              {taskBreakdown.length > 0 ? (
                taskBreakdown.map((t) => (
                  <div key={t.name} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#6b6b78', padding: '3px 0' }}>
                    <span>{t.name}</span>
                    <span>{t.score !== null ? `${t.score}%` : '—'}</span>
                  </div>
                ))
              ) : (
                hoveredMeta.topics.map((t) => (
                  <div key={t} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#6b6b78', padding: '3px 0' }}>
                    <span>{t}</span><span>—</span>
                  </div>
                ))
              )}
              {needsPractice && (
                <p style={{ fontSize: '12.5px', color: '#857d52', background: 'rgba(201, 162, 75, 0.12)', borderRadius: '8px', padding: '8px 10px', margin: '8px 0 4px' }}>
                  Стоит попрактиковаться на <a href={TRAINER_URL} target="_blank" rel="noopener noreferrer" style={{ color: '#312e81', fontWeight: 600 }}>ezspeech.ai</a> и повторить этот модуль
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ModuleStatusTable = ({ aggregatedData }) => (
  <table className="dash-table">
    <thead>
      <tr>
        <th>Направление</th><th>Последний балл</th><th>Дата</th><th>Статус</th><th>Рекомендация</th>
      </tr>
    </thead>
    <tbody>
      {MODULE_META.map((m) => {
        const data = aggregatedData[m.key] || { score: null, date: null };
        // ИСПРАВЛЕНО: было `data.score > 0`, что также считало честный низкий балл "не пройдено"
        const isTaken = data.score !== null && data.score !== undefined;
        const needsPractice = isTaken && data.score < PRACTICE_THRESHOLD;
        return (
          <tr key={m.key}>
            <td>{m.fullLabel}</td>
            <td className={!isTaken ? 'muted' : ''}>{isTaken ? `${data.score}%` : '—'}</td>
            <td className={!isTaken ? 'muted' : ''}>{data.date || '—'}</td>
            <td>
              {!isTaken ? <span className="dash-status-pill muted">не пройден</span> : needsPractice ? <span className="dash-status-pill warning">обратить внимание</span> : <span className="dash-status-pill success">норма</span>}
            </td>
            <td className={!needsPractice ? 'muted' : ''}>
              {needsPractice ? (
                <>Позаниматься самостоятельно или на <a href={TRAINER_URL} target="_blank" rel="noopener noreferrer">ezspeech.ai</a> и пройти тест снова</>
              ) : isTaken ? '—' : 'пройдите этот модуль'}
            </td>
          </tr>
        );
      })}
    </tbody>
  </table>
);

const DashboardPage = ({ user, onLogout, onBack }) => {
  const [children, setChildren] = useState([]);
  const [selectedChildId, setSelectedChildId] = useState(null);
  const [reports, setReports] = useState([]);
  const [aggregatedData, setAggregatedData] = useState({});
  const [compareReportId, setCompareReportId] = useState('none');
  const [loading, setLoading] = useState(true);
  const [activeReport, setActiveReport] = useState(null);

  useEffect(() => {
    if (!user) return;
    const fetchData = async () => {
      setLoading(true);
      const { data: profile } = await supabase.from('students').select('full_name, age_group').eq('id', user.id).single();
      if (profile) {
        setChildren([{ id: user.id, name: profile.full_name || 'Ребенок', age_group: profile.age_group }]);
        setSelectedChildId(user.id);
      }
      const { data: reportsData, error } = await supabase.from('test_reports').select('id, created_at, test_scores, test_details').eq('student_id', user.id).order('created_at', { ascending: false });
      if (error) { console.error('Ошибка:', error); }
      else if (reportsData) {
        const formattedReports = reportsData.map(r => {
          let rawScores = {}; let rawDetails = {};
          try {
            rawScores = typeof r.test_scores === 'string' ? JSON.parse(r.test_scores) : (r.test_scores || {});
            rawDetails = typeof r.test_details === 'string' ? JSON.parse(r.test_details) : (r.test_details || {});
          } catch (e) { console.error('Parse error:', r.id, e); }
          return { id: r.id, date: r.created_at, scores: calculateModuleScores(rawScores), taskScores: rawDetails, rawScores };
        });
        setReports(formattedReports);
        setAggregatedData(aggregateAllReports(formattedReports));
      }
      setLoading(false);
    };
    fetchData();
  }, [user]);

  const selectedChild = children.find((c) => c.id === selectedChildId);
  const latestReport = reports[0];
  const compareReport = compareReportId === 'none' ? null : reports.find((r) => String(r.id) === String(compareReportId));

  const handleOpenReport = (reportId) => {
    const report = reports.find(r => String(r.id) === String(reportId));
    if (report) {
      setActiveReport({
        profile: { name: selectedChild?.name || 'Ребенок', age: selectedChild?.age_group ? `${selectedChild.age_group} лет` : '' },
        test_scores: report.rawScores,
        test_details: report.taskScores,
        expectedTests: Object.keys(report.rawScores || {})
      });
    }
  };

  if (loading || !selectedChild) return <div className="dash-page"><p className="dash-loading">Загрузка…</p></div>;

  if (activeReport) {
    return (
      <ReportPreview
        profile={activeReport.profile}
        test_scores={activeReport.test_scores}
        expectedTests={activeReport.expectedTests}
        test_details={activeReport.test_details}
        onClose={() => setActiveReport(null)}
      />
    );
  }

  if (reports.length === 0) {
    return (
      <div className="dash-page">
        <header className="dash-header">
          <button type="button" className="dash-back-btn" onClick={onBack}>← Назад</button>
          <button type="button" className="dash-logout-btn" onClick={onLogout}>Выйти</button>
        </header>
        <main className="dash-main">
          <div className="dash-card" style={{ textAlign: 'center', padding: '40px' }}>
            <h2>У вас пока нет пройденных тестов</h2>
            <p>Пройдите первый тест, чтобы увидеть здесь результаты</p>
            <button type="button" onClick={onBack} style={{ marginTop: '20px', padding: '10px 20px', background: '#312e81', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>Пройти тест</button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="dash-page">
      <header className="dash-header">
        <button type="button" className="dash-back-btn" onClick={onBack}>← Назад</button>
        <button type="button" className="dash-logout-btn" onClick={onLogout}>Выйти</button>
      </header>

      <main className="dash-main">
        <div className="dash-child-bar">
          <div className="dash-child-info">
            <div className="dash-child-avatar">{selectedChild.name.slice(0, 2).toUpperCase()}</div>
            <p className="dash-child-name">{selectedChild.name}, {selectedChild.age_group} лет</p>
          </div>
        </div>

        <div className="dash-radar-grid">
          <ReportRadarCard id="current" scores={latestReport.scores} rawScores={latestReport.rawScores} taskScores={latestReport.taskScores} currentLabel={`Сейчас — ${new Date(latestReport.date).toLocaleDateString('ru-RU')}`} onOpen={() => handleOpenReport(latestReport.id)} />
          <ReportRadarCard id="compare" muted scores={compareReport?.scores} rawScores={compareReport?.rawScores} taskScores={compareReport?.taskScores} onOpen={() => compareReport && handleOpenReport(compareReport.id)} dateOptions={reports.filter((r) => String(r.id) !== String(latestReport.id)).map((r) => ({ id: r.id, label: new Date(r.date).toLocaleDateString('ru-RU') }))} selectedDate={compareReportId} onDateChange={setCompareReportId} emptyHint={reports.length > 1 ? 'Выберите дату выше' : 'Пока только один пройденный тест'} />
        </div>

        <ModuleStatusTable aggregatedData={aggregatedData} />
      </main>
    </div>
  );
};

export default DashboardPage;